import { cx } from '../ui/cx.ts';
import { ChartXAxis } from './ChartXAxis.tsx';
import { CHART_FILL_RAMP, CHART_SWATCH_RAMP } from './ramp.ts';

/**
 * Stacked bar groups with legend chips — Field Notes chart set.
 *
 * Series colors cycle the FIXED chart ramp of DESIGN.md §2 (forest →
 * moss → rust → warn → line-strong, in that order, no extra hues).
 * There is deliberately no per-series color prop: consumers order
 * series by importance and the cycle does the rest, so charts cannot
 * drift off-token. The class literals themselves live in `ramp.ts`,
 * shared with `Line`'s color props (see that file for why they must
 * stay whole literals).
 *
 * Accessibility: the svg is exposed as ONE image (`role="img"` +
 * mandatory `ariaLabel` + `<title>`); each bucket `<g>` carries a
 * `<title>` tooltip. Legend chips are visible text; their color
 * swatches are `aria-hidden` decoration. Consumers surface totals as
 * text alongside — the chart is never the sole carrier of a number
 * (DESIGN.md §9).
 *
 * Incomplete buckets (the partial ones at the ends of a window) are
 * marked TWICE: a lowered `fillOpacity` — an SVG attribute, not a color
 * token, so the fixed ramp stays intact — and `incompleteLabel` appended
 * to that bucket's `<title>`. The text half is not optional: DESIGN.md
 * §9 forbids information carried by fade alone.
 *
 * Every bucket also gets a cell in the `ChartXAxis` row below, whether
 * or not it carries a tick label — without it, a missing bucket leaves
 * no trace at all.
 *
 * Edges: empty buckets/series render the baseline grid only; an
 * all-zero stack draws no rects (max floored at 1, zero-height bars
 * are skipped) but still occupies its slot on the axis.
 */

export interface StackedBarsSeries {
  readonly id: string;
  /** Legend text (already translated / display-ready). */
  readonly label: string;
  /** One value per bucket, aligned by index with `buckets`. */
  readonly values: readonly number[];
}

const FILL_CYCLE = CHART_FILL_RAMP;
const SWATCH_CYCLE = CHART_SWATCH_RAMP;

/** Exposed so table/legend consumers can echo a series color. */
export function stackedBarsFillClass(index: number): string {
  return (
    FILL_CYCLE[((index % FILL_CYCLE.length) + FILL_CYCLE.length) % FILL_CYCLE.length] ??
    'fill-forest'
  );
}

/**
 * `bg-*` twin of {@link stackedBarsFillClass} for non-SVG consumers
 * (share strips, list swatches) that must echo the same series cycle.
 */
export function stackedBarsSwatchClass(index: number): string {
  return (
    SWATCH_CYCLE[((index % SWATCH_CYCLE.length) + SWATCH_CYCLE.length) % SWATCH_CYCLE.length] ??
    'bg-forest'
  );
}

const VIEW_WIDTH = 960;
const PADDING = 16;
const GAP = 6;

interface Props {
  /** Bucket labels (x axis), display-ready — e.g. `2026-07-01`. */
  readonly buckets: readonly string[];
  readonly series: readonly StackedBarsSeries[];
  /**
   * Aligned by index with `buckets`: true where the window only covers
   * part of the bucket. Omitted means "every bucket is whole".
   */
  readonly incomplete?: readonly boolean[];
  /** Display-ready marker appended to an incomplete bucket's `<title>`. */
  readonly incompleteLabel?: string;
  /** Rendered pixel height (doubles as the viewBox height). */
  readonly height?: number;
  readonly showLegend?: boolean;
  readonly ariaLabel: string;
  readonly testId?: string;
}

/** Fade for partial buckets — an opacity, never a second color. */
const INCOMPLETE_FILL_OPACITY = 0.4;

export function StackedBars({
  buckets,
  series,
  incomplete,
  incompleteLabel,
  height = 288,
  showLegend = true,
  ariaLabel,
  testId,
}: Props): JSX.Element {
  const drawableHeight = height - PADDING * 2;
  const maxStack = Math.max(
    1,
    ...buckets.map((_, bucketIndex) =>
      series.reduce((sum, s) => sum + Math.max(0, s.values[bucketIndex] ?? 0), 0),
    ),
  );
  const barWidth =
    buckets.length === 0
      ? 0
      : Math.max(4, (VIEW_WIDTH - PADDING * 2 - GAP * (buckets.length - 1)) / buckets.length);

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <svg
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
        preserveAspectRatio="none"
        style={{ height }}
        className="block w-full"
      >
        <title>{ariaLabel}</title>
        {[0, 1, 2].map((line) => {
          const y = PADDING + (line / 2) * drawableHeight;
          return (
            <line
              key={line}
              x1={PADDING}
              x2={VIEW_WIDTH - PADDING}
              y1={y}
              y2={y}
              className="stroke-line-soft"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
        {buckets.map((bucket, bucketIndex) => {
          let yCursor = PADDING + drawableHeight;
          const partial = incomplete?.[bucketIndex] === true;
          return (
            <g key={`${bucketIndex}-${bucket}`}>
              <title>
                {partial && incompleteLabel !== undefined
                  ? `${bucket} · ${incompleteLabel}`
                  : bucket}
              </title>
              {series.map((s, seriesIndex) => {
                const value = Math.max(0, s.values[bucketIndex] ?? 0);
                const barHeight = (value / maxStack) * drawableHeight;
                yCursor -= barHeight;
                return barHeight > 0 ? (
                  <rect
                    key={s.id}
                    x={PADDING + bucketIndex * (barWidth + GAP)}
                    y={yCursor}
                    width={barWidth}
                    height={barHeight}
                    rx={1}
                    className={stackedBarsFillClass(seriesIndex)}
                    fillOpacity={partial ? INCOMPLETE_FILL_OPACITY : undefined}
                  />
                ) : null;
              })}
            </g>
          );
        })}
      </svg>
      <ChartXAxis
        labels={buckets}
        layout="bars"
        testId={testId === undefined ? undefined : `${testId}-ticks`}
      />
      {showLegend && series.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3">
          {series.map((s, index) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-1.5 text-mono-label text-ink-low"
            >
              <span
                aria-hidden="true"
                className={cx('h-2 w-2 shrink-0 rounded-[1.5px]', stackedBarsSwatchClass(index))}
              />
              <span className="font-mono">{s.label}</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
